import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Menu, MenuItem, Select, Tooltip } from "@material-ui/core";
import { Assignment, Build, Event, KeyboardArrowDown, MoreVert, Replay } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import GnsOsSupportTicketModal from "../GnsOsSupportTicketModal";
import GnsOsVisitRequestModal from "../GnsOsVisitRequestModal";
import GnsOsServiceOrderModal from "../GnsOsServiceOrderModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		marginRight: theme.spacing(1),
		flexShrink: 0,
		alignSelf: "center",
		marginLeft: "auto",
		[theme.breakpoints.down("sm")]: {
			gap: theme.spacing(0.5),
			marginLeft: 0,
			marginRight: 0,
			"& .MuiButton-root": {
				whiteSpace: "nowrap",
			},
		},
	},
	prioritySelect: {
		minWidth: 110,
		[theme.breakpoints.down("sm")]: {
			minWidth: 96,
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [gnsOsMenuAnchorEl, setGnsOsMenuAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [gnsOsSupportTicketModalOpen, setGnsOsSupportTicketModalOpen] = useState(false);
	const [gnsOsVisitRequestModalOpen, setGnsOsVisitRequestModalOpen] = useState(false);
	const [gnsOsServiceOrderModalOpen, setGnsOsServiceOrderModalOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleOpenGnsOsMenu = e => setGnsOsMenuAnchorEl(e.currentTarget);
	const handleCloseGnsOsMenu = () => setGnsOsMenuAnchorEl(null);
	const handleOpenSupportTicket = () => {
		handleCloseGnsOsMenu();
		setGnsOsSupportTicketModalOpen(true);
	};
	const handleOpenVisitRequest = () => {
		handleCloseGnsOsMenu();
		setGnsOsVisitRequestModalOpen(true);
	};
	const handleOpenServiceOrder = () => {
		handleCloseGnsOsMenu();
		setGnsOsServiceOrderModalOpen(true);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleUpdateTicketPriority = async e => {
		try {
			await api.put(`/tickets/${ticket.id}`, {
				priority: e.target.value,
			});
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			<ButtonWithSpinner size="small" endIcon={<KeyboardArrowDown />} onClick={handleOpenGnsOsMenu}>
				GNS-OS
			</ButtonWithSpinner>
			<Menu anchorEl={gnsOsMenuAnchorEl} keepMounted open={Boolean(gnsOsMenuAnchorEl)} onClose={handleCloseGnsOsMenu}>
				<MenuItem onClick={handleOpenServiceOrder}><Assignment fontSize="small" style={{ marginRight: 10 }} />Registrar orden de servicio</MenuItem>
				<MenuItem onClick={handleOpenSupportTicket}><Build fontSize="small" style={{ marginRight: 10 }} />Crear ticket de soporte</MenuItem>
				<MenuItem onClick={handleOpenVisitRequest}><Event fontSize="small" style={{ marginRight: 10 }} />Agendar visita tecnica</MenuItem>
			</Menu>
			<Select
				native
				size="small"
				variant="outlined"
				value={ticket.priority || "normal"}
				className={classes.prioritySelect}
				onChange={handleUpdateTicketPriority}
			>
				<option value="normal">{i18n.t("tickets.priorities.normal")}</option>
				<option value="high">{i18n.t("tickets.priorities.high")}</option>
				<option value="urgent">{i18n.t("tickets.priorities.urgent")}</option>
			</Select>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
						>
							<Replay />
						</IconButton>
					</Tooltip>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
			<GnsOsSupportTicketModal
				modalOpen={gnsOsSupportTicketModalOpen}
				onClose={() => setGnsOsSupportTicketModalOpen(false)}
				ticket={ticket}
			/>
			<GnsOsVisitRequestModal
				modalOpen={gnsOsVisitRequestModalOpen}
				onClose={() => setGnsOsVisitRequestModalOpen(false)}
				ticket={ticket}
			/>
			<GnsOsServiceOrderModal
				modalOpen={gnsOsServiceOrderModalOpen}
				onClose={() => setGnsOsServiceOrderModalOpen(false)}
				ticket={ticket}
			/>
		</div>
	);
};

export default TicketActionButtons;
